import { ChangeEvent, FC, Fragment, useEffect, useState } from "react"
import { useExpanded, useTable, Column, Row, HeaderGroup, Cell } from "react-table"
import {
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material"
import { KSpan } from "kahuna-base-react-components"
import { formatNumber } from "utils/numbers"

interface TablePaginationProps {
  gotoPage: (value: number) => void
  setPageSize: (value: number) => void
  pageIndex: number
  pageSize: number
  initialPageSize?: number
  propOptions?: number[]
  totalCount: number
}

export const TablePagination = ({
  gotoPage,
  totalCount,
  setPageSize,
  pageSize,
  pageIndex,
  propOptions,
  initialPageSize
}: TablePaginationProps) => {
  const [open, setOpen] = useState(false)
  const [goToPageNumber, setGoToPageNumber] = useState(1)
  let options: number[] = propOptions || [5, 10, 25, 50, 100]

  if (initialPageSize) {
    options = [...options, initialPageSize]
      .filter((item, index) => [...options, initialPageSize].indexOf(item) === index)
      .sort(function (a, b) {
        return a - b
      })
  }

  useEffect(() => {
    setGoToPageNumber(pageIndex + 1)
  }, [pageIndex])

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleChangePagination = (event: ChangeEvent<unknown>, value: number) => {
    gotoPage(value - 1)
  }

  const handleChange = (event: SelectChangeEvent<number>) => {
    setPageSize(+event.target.value)
  }

  return (
    <Grid container alignItems="center" justifyContent="space-between" sx={{ width: "auto" }}>
      <Grid item>
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="secondary">
              Row per page
            </Typography>
            <FormControl sx={{ m: 1 }}>
              <Select
                id="demo-controlled-open-select"
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                value={pageSize}
                onChange={handleChange}
                size="small"
                sx={{ "& .MuiSelect-select": { py: 0.75, px: 1.25 } }}
              >
                {options.map((option: number) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" color="secondary">
            Go to
          </Typography>
          <TextField
            size="small"
            type="number"
            value={goToPageNumber}
            onChange={(e) => {
              setGoToPageNumber(e.target.value ? Number(e.target.value) : 0)
            }}
            onBlur={(e) => {
              const page = e.target.value ? Number(e.target.value) : 0
              gotoPage(page - 1)
            }}
            sx={{ "& .MuiOutlinedInput-input": { py: 0.75, px: 1.25, width: 36 } }}
          />
          <div className="ml-2">
            <KSpan fontSize={13} text="Total: " />
            <KSpan color="#111" fontSize={13} text={totalCount ? formatNumber(totalCount) : "-"} />
          </div>
        </Stack>
      </Grid>
      <Grid item sx={{ mt: { xs: 2, sm: 0 } }}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={pageIndex + 1}
          onChange={handleChangePagination}
          color="primary"
          variant="combined"
          showFirstButton
          showLastButton
        />
      </Grid>
    </Grid>
  )
}

interface TableProps {
  columns: Column[]
  data: any[]
  renderRowSubComponent?: FC<any>
  pagination?: boolean
  paginationProps?: TablePaginationProps
  rowHeight?: number
  showEmptyRows?: boolean
  onRowClick?: (id: number) => void
  toogleAll?: boolean
  propOptions?: number[]
}

const ReactTable = ({
  columns,
  data,
  renderRowSubComponent,
  pagination,
  paginationProps,
  rowHeight,
  showEmptyRows,
  onRowClick,
  toogleAll,
  propOptions
}: TableProps) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns, toggleAllRowsExpanded } =
    useTable(
      {
        columns,
        data
      },
      useExpanded
    )

  useEffect(() => {
    if (toogleAll) toggleAllRowsExpanded()
  }, [toogleAll])

  return (
    <Table {...getTableProps()}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<{}>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<{}>) => (
              <TableCell
                {...column.getHeaderProps([{ className: column.className }])}
                width={headerGroup.width}
                sx={{ width: headerGroup.width, maxWidth: "400px" }}
              >
                {column.render("Header")}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {rows.map((row: Row, i: number) => {
          prepareRow(row)
          const rowProps = row.getRowProps()
          let disabled = false
          if ("blackList" in row.original) {
            disabled = row.original.blackList === true
          }
          if ("ignore" in row.original) {
            disabled = row.original.ignore === true
          }

          return (
            <Fragment key={i}>
              <TableRow
                {...row.getRowProps()}
                style={{ background: disabled ? "#C0C0C0" : "", cursor: onRowClick ? "pointer" : "" }}
                onClick={() => {
                  if (onRowClick && "id" in row.original) {
                    onRowClick(row.original.id as number)
                  }
                }}
              >
                {row.cells.map((cell: Cell<{}>) => (
                  <TableCell
                    {...cell.getCellProps([{ className: cell.column.className }])}
                    width={cell.column.width}
                    sx={{
                      width: cell.column.width,
                      ...(rowHeight && { height: rowHeight }),
                      maxWidth: "400px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: 'ellipsis " [..]"'
                    }}
                  >
                    {cell.render("Cell")}
                  </TableCell>
                ))}
              </TableRow>
              {!onRowClick &&
                row.isExpanded &&
                renderRowSubComponent &&
                renderRowSubComponent({ row, rowProps, visibleColumns })}
            </Fragment>
          )
        })}
        {showEmptyRows &&
          Array(paginationProps?.pageSize!! - rows.length)
            .fill(0)
            .map((e, i) => i + 1)
            .map((row, i) => {
              return (
                <Fragment key={i}>
                  <TableRow>
                    {columns.map((column, j) => (
                      <TableCell
                        key={`show_empty_row_$${i}_${j}`}
                        width={column.width}
                        sx={{
                          width: column.width,
                          ...(rowHeight && { height: rowHeight }),
                          maxWidth: "400px",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: 'ellipsis " [..]"'
                        }}
                      ></TableCell>
                    ))}
                  </TableRow>
                </Fragment>
              )
            })}
        {pagination && (
          <TableRow sx={{ "&:hover": { bgcolor: "transparent !important" } }}>
            <TableCell sx={{ p: 2, py: 3 }} colSpan={12}>
              <TablePagination {...paginationProps!} propOptions={propOptions} />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

export default ReactTable
